import Dexie from 'dexie';
import IPrayer from '../interfaces/IPrayer';
import IReadingPlanRecord from '../interfaces/IReadingPlanRecord';
import IPrayerSettings from '../interfaces/IPrayerSettings';
import IDismissibleMessage from '../interfaces/IDismissibleMessage';
import { DefaultReadingPlanRecord } from './DefaultReadingPlanRecord';
import DocumentDataMigrationService from '../services/DocumentDataMigrationService';
import IBibleSettings from '../interfaces/IBibleSettings';
import IFellowshipSettings from '../interfaces/IFellowshipSettings';
import IFellowshipPost from '../interfaces/IFellowshipPost';
import { SQLiteTables } from './SQLiteTables';
import ReadingPlanRecordService from '../services/ReadingPlanRecordService';

export class DexieDB extends Dexie {
    prayers: Dexie.Table<IPrayer>;
    prayerSettings: Dexie.Table<IPrayerSettings>;
    readingPlanRecords: Dexie.Table<IReadingPlanRecord>;
    dismissibleMessages: Dexie.Table<IDismissibleMessage>;
    bibleSettings: Dexie.Table<IBibleSettings>;
    fellowshipSettings: Dexie.Table<IFellowshipSettings>;
    fellowshipPosts:  Dexie.Table<IFellowshipPost>;
    
    constructor() {  
      super("BstDexieDB");
      // ***IMPORTANT NOTE:*** 
      // ALL data migrations should be done using the DocumentDataMigrationService and added to MigrateToLatest methods because it is also used to migrate data stored in the cloud. Failure to do so will result in mismatched data structures.
      
      // Define tables and indexes
      // (Here's where the implicit table props are dynamically created)
      this.version(2).stores({
        prayers: '++id, title, status, order',
        readingPlanRecords: '++id, readingPlan',
      });
      this.version(3).stores({
        prayers: '++id, title, status, order',
        prayerSettings: '++id',
        readingPlanRecords: '++id, readingPlan',
      });
      this.version(4).stores({
        prayers: '++id, title, status, order',
        prayerSettings: '++id',
        readingPlanRecords: '++id, readingPlan',
        dismissibleMessages: '++id, messageId, category',
      });
      this.version(5).stores({
        prayers: '++id, title, status, order',
        prayerSettings: '++id',
        readingPlanRecords: '++id, readingPlan',
        dismissibleMessages: '++id, messageId, category',
      }).upgrade(tx => {
        tx.table('readingPlanRecords').toCollection().modify(readingPlanRecord => {
          const record:IReadingPlanRecord = DocumentDataMigrationService.readingPlanMigrationTo6(readingPlanRecord);
          // Migrated values need to be assigned by reference to work.
          DocumentDataMigrationService.assignValuesToReference(readingPlanRecord, record);
        });
      });
      this.version(6).stores({
        prayers: '++id, title, status, order',
        prayerSettings: '++id',
        readingPlanRecords: '++id, readingPlan, readingPlanCode',
        dismissibleMessages: '++id, messageId, category',
      }).upgrade(tx => {
        tx.table('readingPlanRecords').toCollection().modify(record => {
          if(record.readingPlan === 'Chronological Cross-Reference Plan') {
            record.readingPlanExtrasLink = DefaultReadingPlanRecord.readingPlanExtrasLink;
          }
        });
      });
      this.version(7).stores({
        prayers: '++id, title, status, order',
        prayerSettings: '++id',
        readingPlanRecords: '++id, readingPlan, readingPlanCode',
        dismissibleMessages: '++id, messageId, category',
        bibleSettings: '++id',
      });
      this.version(8).stores({
        prayers: '++id, title, status, order',
        prayerSettings: '++id',
        readingPlanRecords: '++id, readingPlan, readingPlanCode',
        dismissibleMessages: '++id, messageId, category',
        bibleSettings: '++id',
        fellowshipSettings: '++id, defaultGroupId', 
        fellowshipPosts: '&id, groupId, type, title, sortDate, [groupId+sortDate]', 
      });
      this.version(9).stores({
        prayers: '++id, title, status, order',
        prayerSettings: '++id',
        readingPlanRecords: '++id, readingPlan, readingPlanCode',
        dismissibleMessages: '++id, messageId, category',
        bibleSettings: '++id',
        fellowshipSettings: '++id, defaultGroupId', 
        fellowshipPosts: '&id, groupId, type, title, sortDate, [groupId+sortDate]', 
      }).upgrade(tx => {
        tx.table('bibleSettings').toCollection().modify(record => {
          record.audioScroll = true;
        });
      });
      this.version(10).stores({
        prayers: '++id, title, status, order',
        prayerSettings: '++id',
        readingPlanRecords: '++id, readingPlan, readingPlanCode',
        dismissibleMessages: '++id, messageId, category',
        bibleSettings: '++id',
        fellowshipSettings: '++id, defaultGroupId', 
        fellowshipPosts: '&id, groupId, type, title, sortDate, [groupId+sortDate]', 
      }).upgrade(tx => {
        tx.table(SQLiteTables.fellowshipSettings).get(0).then((fellowshipSetting: IFellowshipSettings) => {
          tx.table(SQLiteTables.readingPlanRecords).toCollection().modify((record: IReadingPlanRecord) => {
            // Check that the Reading plan schedule matches the group schedule
            // If so make it the group reading plan, otherwise when synced with the group it will change the plan.
            const userGroup = fellowshipSetting?.defaultGroupId ? fellowshipSetting.userFellowshipGroups[fellowshipSetting.defaultGroupId].group : undefined;

            // This means there is a group reading plan
            if(userGroup?.syncCode && record) {
              const result = ReadingPlanRecordService.interpretSyncCode!(userGroup.syncCode);
              const millisecondsPerDay = 86400000;
              const startDateDifference = !!result ? Math.abs(result.startDate.getTime() - record.startDate.getTime()) : millisecondsPerDay + 10;

              // make it so the users reading plan is becomes there group reading plan
              if(record.readingPlanCode === result?.readingPlanCode && startDateDifference < millisecondsPerDay) {
                record.groupId = userGroup.id;
                record.name = userGroup.name;
              } else {
                record.groupId = '';
                record.name = 'My Reading Plan';
              }
            } else if (record) {
              record.groupId = '';
              record.name = 'My Reading Plan';
            }
          });
        });
        tx.table('bibleSettings').toCollection().modify(record => {
          record.activeReadingPlanId = 0;
        });
      });
      
      // The following lines are needed for it to work across typescript using babel-preset-typescript:
      this.prayers = this.table('prayers');
      this.prayerSettings = this.table('prayerSettings');
      this.readingPlanRecords = this.table('readingPlanRecords');
      this.dismissibleMessages = this.table('dismissibleMessages');
      this.bibleSettings = this.table('bibleSettings');
      this.fellowshipSettings = this.table('fellowshipSettings');
      this.fellowshipPosts =  this.table('fellowshipPosts');
    }
}

export default new DexieDB();